import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchPlanFeatureAssignments, savePlanFeatureAssignments } from '../../services/api/adminPlanFeatureAssignments';
import PlanFeatureChecklist from '../../components/admin/PlanFeatureChecklist';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';

const AdminPlanFeatureAssignments = () => {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const isDirty = useMemo(() => {
    if (selectedIds.size !== originalIds.size) return true;
    for (const x of selectedIds) if (!originalIds.has(x)) return true;
    return false;
  }, [selectedIds, originalIds]);
  useUnsavedChangesPrompt(isDirty);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchPlanFeatureAssignments(id);
        if (cancelled) return;
        setData(res.data);
        const assigned = new Set();
        (res.data.features || []).forEach((f) =>
          (f.sub_features || []).forEach((sf) => sf.assigned && assigned.add(sf.id))
        );
        setSelectedIds(new Set(assigned));
        setOriginalIds(new Set(assigned));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const handleToggle = (sfId, nextChecked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (nextChecked) next.add(sfId); else next.delete(sfId);
      return next;
    });
  };

  const handleToggleAll = (feature, nextChecked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      feature.sub_features.forEach((sf) => {
        if (nextChecked) next.add(sf.id); else next.delete(sf.id);
      });
      return next;
    });
  };

  const handleSave = async () => {
    const subscriptionCount = data?.plan?.subscription_count || 0;
    if (subscriptionCount > 5) {
      const ok = window.confirm(`This will affect ${subscriptionCount} workspaces immediately. Continue?`);
      if (!ok) return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const res = await savePlanFeatureAssignments(id, Array.from(selectedIds));
      setData(res.data);
      const assigned = new Set();
      (res.data.features || []).forEach((f) =>
        (f.sub_features || []).forEach((sf) => sf.assigned && assigned.add(sf.id))
      );
      setOriginalIds(new Set(assigned));
      setSelectedIds(new Set(assigned));
      setSuccess('Plan features updated.');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setSelectedIds(new Set(originalIds));
    setSuccess(null);
    setError(null);
  };

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading...</div>;
  if (!data) return <div className="p-4 text-sm text-red-600">{error || 'Plan not found.'}</div>;

  const plan = data.plan;
  const subscriptionCount = plan.subscription_count || 0;

  return (
    <div className="pb-24">
      <Link to="/admin/plans" className="text-xs text-muted-foreground hover:underline">← Back to plans</Link>
      <div className="flex items-baseline justify-between mt-2 mb-1">
        <h1 className="text-2xl font-bold text-foreground">{plan.name}</h1>
        <span className="text-xs text-muted-foreground">Plan code: {plan.code}</span>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        ${(plan.monthly_price_cents / 100).toFixed(2)}/mo · {plan.monthly_credits} monthly credits · {plan.billing_mode}
      </p>

      <h3 className="text-sm font-semibold text-foreground mb-3">Features included in this plan</h3>
      <PlanFeatureChecklist
        features={data.features || []}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onToggleAll={handleToggleAll}
      />

      <p className="mt-4 text-sm text-muted-foreground">
        {subscriptionCount} workspace{subscriptionCount === 1 ? '' : 's'} {subscriptionCount === 1 ? 'is' : 'are'} on this plan and will receive these features.
      </p>

      {success && <div className="mt-4 p-3 bg-green-50 text-green-700 text-sm rounded-lg">{success}</div>}
      {error   && <div className="mt-4 p-3 bg-red-50  text-red-700  text-sm rounded-lg">{error}</div>}

      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border px-6 py-3 flex justify-end gap-2 shadow-sm">
        <button
          type="button"
          onClick={handleDiscard}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm text-foreground border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default AdminPlanFeatureAssignments;
